import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/jtl-zu-shopify";
const TITLE = "JTL-Shop zu Shopify: Umzug mit JTL-Wawi im Hintergrund";
const DESCRIPTION =
  "JTL-Shop zu Shopify wechseln und JTL-Wawi behalten: Anbindung, Datenübernahme, Weiterleitungen, Stolperfallen bei Stücklisten und Kosten des Umzugs.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Wer von JTL-Shop zu Shopify wechselt, behält in der Regel JTL-Wawi als führendes System und bindet Shopify über einen Connector an. Der Aufwand steckt in der Artikelstruktur mit Stücklisten und Variationskombinationen, in Steuer- und Versandregeln sowie im vollständigen Weiterleitungsplan. Vier bis acht Wochen sind üblich.";

const faqs = [
  {
    q: "Kann ich JTL-Wawi weiter nutzen, wenn ich zu Shopify wechsle?",
    a: "Ja. JTL-Wawi bleibt in den meisten Projekten das führende System für Artikel, Bestände und Bestellungen und wird über einen Connector mit Shopify synchronisiert. Wichtig ist, vorab festzulegen, welches System bei welchem Feld gewinnt.",
  },
  {
    q: "Was ist bei Variationskombinationen und Stücklisten zu beachten?",
    a: "Shopify kennt Varianten, aber keine echten Stücklisten im JTL-Sinn. Sets und Bundles werden entweder in der Wawi aufgelöst oder in Shopify über Bundle-Logik nachgebaut. Das gehört vor den ersten Import geklärt.",
  },
  {
    q: "Wie gehe ich mit den alten JTL-Shop-Adressen um?",
    a: "Alle Artikel-, Kategorie- und Inhaltsadressen bekommen ein 301-Ziel in Shopify. JTL-Shops haben oft zusätzlich Filter- und Suchseiten mit Sichtbarkeit, die in der Liste häufig vergessen werden.",
  },
  {
    q: "Was kostet der Wechsel von JTL-Shop zu Shopify?",
    a: "Mit Wawi-Anbindung liegen die Projekte meist bei 6.000 bis 15.000 € netto, abhängig von Artikelstruktur und Sonderfällen. Entwicklung berechne ich mit 110 € netto pro Stunde, Conversion-Optimierung ab 3.990 € netto.",
  },
  {
    q: "Wie lange dauert der Umzug?",
    a: "Vier bis acht Wochen, inklusive Connector-Einrichtung, Testabgleichen zwischen Wawi und Shop sowie Testbestellungen vor dem Livegang.",
  },
];

export const Route = createFileRoute("/ratgeber/jtl-zu-shopify")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Migration",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Migration"
      title="JTL-Shop zu Shopify: Wawi behalten, Shop modernisieren"
      intro="Viele Händler wollen nicht ihre Warenwirtschaft tauschen, sondern den Shop davor. Genau das ist der übliche Weg: JTL-Wawi bleibt, Shopify übernimmt Storefront und Kaufabschluss. Der Erfolg hängt daran, wie sauber die Artikelstruktur übersetzt wird."
      source="ratgeber-jtl-zu-shopify"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Die Ausgangslage",
          p: [
            "JTL-Wawi ist bei vielen Händlern das Rückgrat für Artikel, Bestände, Versand und Rechnungen. Der JTL-Shop davor ist dagegen oft gewachsen, langsam und schwer anzupassen.",
            "Shopify ersetzt in diesem Szenario nur die Storefront. Die Wawi bleibt führend, der Abgleich läuft über einen Connector.",
          ],
        },
        {
          h: "Vor dem ersten Import klären",
          p: [],
          list: [
            "Welches System ist bei Preisen, Beständen und Texten führend",
            "Wie werden Variationskombinationen in Shopify-Varianten übersetzt",
            "Wie werden Stücklisten und Sets abgebildet oder aufgelöst",
            "Welche Steuer- und Versandklassen gelten je Artikelgruppe",
            "Wie werden Kundengruppen und Nettopreise für B2B gelöst",
          ],
        },
        {
          h: "Weiterleitungen und Sichtbarkeit",
          p: [
            "Jede alte Adresse braucht ein Ziel. Neben Artikel- und Kategorieseiten haben JTL-Shops häufig Merkmal-, Hersteller- und Filterseiten mit Rankings, die in Weiterleitungslisten regelmäßig fehlen.",
          ],
        },
        {
          h: "Kosten und Zeitrahmen",
          p: [
            "Mit Wawi-Anbindung liegen die Projekte meist bei 6.000 bis 15.000 € netto und vier bis acht Wochen. Entwicklung rechne ich mit 110 € netto pro Stunde ab, Conversion-Optimierung ab 3.990 € netto.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopware-zu-shopify", label: "Shopware zu Shopify" },
        { to: "/ratgeber/shopify-b2b-shop-einrichten", label: "Shopify B2B einrichten" },
        { to: "/shopify-app-entwicklung", label: "Shopify App-Entwicklung" },
        { to: "/kontakt", label: "Projekt anfragen" },
      ]}
    />
  );
}
